/**
 * AudioSystem - Web Audio API procedural sound engine.
 * Covers tasks 4.1–4.12: ambient sound, kick/shoot, goal, crowd, whistle,
 * footsteps, ball-hit-post, GK catch, referee announcements, weather sounds,
 * volume controls, and procedural commentary.
 */

export interface AudioSettings {
  masterVolume: number;   // 0–1
  sfxVolume: number;      // 0–1
  musicVolume: number;    // 0–1
  commentaryVolume: number; // 0–1
}

export class AudioSystem {
  private static _ctx: AudioContext | null = null;
  private static _masterGain: GainNode | null = null;
  private static _sfxGain: GainNode | null = null;
  private static _musicGain: GainNode | null = null;
  private static _commentaryGain: GainNode | null = null;
  private static _ambientNode: AudioBufferSourceNode | null = null;
  private static _weatherNode: AudioBufferSourceNode | null = null;
  private static _settings: AudioSettings = {
    masterVolume: 0.8,
    sfxVolume: 1.0,
    musicVolume: 0.6,
    commentaryVolume: 0.9,
  };

  // ── Initialization ────────────────────────────────────────────────

  static init(): void {
    if (this._ctx) return;
    this._ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    this._masterGain = this._ctx.createGain();
    this._masterGain.gain.value = this._settings.masterVolume;
    this._masterGain.connect(this._ctx.destination);

    this._sfxGain = this._ctx.createGain();
    this._sfxGain.gain.value = this._settings.sfxVolume;
    this._sfxGain.connect(this._masterGain);

    this._musicGain = this._ctx.createGain();
    this._musicGain.gain.value = this._settings.musicVolume;
    this._musicGain.connect(this._masterGain);

    this._commentaryGain = this._ctx.createGain();
    this._commentaryGain.gain.value = this._settings.commentaryVolume;
    this._commentaryGain.connect(this._masterGain);

    // Start ambient stadium sound
    this._startAmbient();

    // Resume if suspended (browser autoplay policy)
    if (this._ctx.state === "suspended") {
      const resume = () => {
        this._ctx?.resume();
        document.removeEventListener("click", resume);
        document.removeEventListener("keydown", resume);
      };
      document.addEventListener("click", resume);
      document.addEventListener("keydown", resume);
    }
  }

  // ── Volume Controls (task 4.11) ───────────────────────────────────

  static setMasterVolume(v: number): void {
    this._settings.masterVolume = Math.max(0, Math.min(1, v));
    if (this._masterGain) this._masterGain.gain.value = this._settings.masterVolume;
  }

  static setSFXVolume(v: number): void {
    this._settings.sfxVolume = Math.max(0, Math.min(1, v));
    if (this._sfxGain) this._sfxGain.gain.value = this._settings.sfxVolume;
  }

  static setMusicVolume(v: number): void {
    this._settings.musicVolume = Math.max(0, Math.min(1, v));
    if (this._musicGain) this._musicGain.gain.value = this._settings.musicVolume;
  }

  static setCommentaryVolume(v: number): void {
    this._settings.commentaryVolume = Math.max(0, Math.min(1, v));
    if (this._commentaryGain) this._commentaryGain.gain.value = this._settings.commentaryVolume;
  }

  static getSettings(): AudioSettings {
    return { ...this._settings };
  }

  // ── Internal helpers ──────────────────────────────────────────────

  private static _ctx_safe(): AudioContext | null {
    if (!this._ctx) this.init();
    return this._ctx;
  }

  /** Create a simple noise buffer */
  private static _makeNoise(ctx: AudioContext, duration: number, type: "white" | "pink" = "white"): AudioBuffer {
    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buf = ctx.createBuffer(1, length, sampleRate);
    const data = buf.getChannelData(0);
    if (type === "white") {
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    } else {
      // Pink noise (1/f) approximation
      let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
      for (let i = 0; i < length; i++) {
        const w = Math.random() * 2 - 1;
        b0=0.99886*b0+w*0.0555179; b1=0.99332*b1+w*0.0750759;
        b2=0.96900*b2+w*0.1538520; b3=0.86650*b3+w*0.3104856;
        b4=0.55000*b4+w*0.5329522; b5=-0.7616*b5-w*0.0168980;
        data[i]=(b0+b1+b2+b3+b4+b5+b6+w*0.5362)* 0.11;
        b6=w*0.115926;
      }
    }
    return buf;
  }

  // ── 4.1 Stadium Ambient Sound ────────────────────────────────────

  private static _startAmbient(): void {
    const ctx = this._ctx!;
    if (!ctx || !this._musicGain) return;
    
    // Low crowd hum base
    const humFreqs = [65, 80, 120, 160];
    humFreqs.forEach(freq => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.value = 0.008;
      osc.connect(gain);
      gain.connect(this._musicGain!);
      osc.start();
    });

    // Crowd noise (pink noise filtered)
    const noiseBuf = this._makeNoise(ctx, 2, "pink");
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuf;
    noiseSource.loop = true;
    
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 400;
    filter.Q.value = 0.8;
    
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.04;
    
    noiseSource.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this._musicGain!);
    noiseSource.start();
  }

  // ── 4.2 Kick / Shoot Sound ────────────────────────────────────────

  static playKick(power: number = 0.5): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    // Thud impact
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(120 + power * 80, now);
    osc.frequency.exponentialRampToValueAtTime(40, now + 0.08);
    gain.gain.setValueAtTime(0.5 * power + 0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc.connect(gain);
    gain.connect(this._sfxGain);
    osc.start(now);
    osc.stop(now + 0.15);

    // High-freq "swish"
    const noiseBuf = this._makeNoise(ctx, 0.1, "white");
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuf;
    const hpf = ctx.createBiquadFilter();
    hpf.type = "highpass";
    hpf.frequency.value = 2000;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.3 * power, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    noiseSource.connect(hpf);
    hpf.connect(noiseGain);
    noiseGain.connect(this._sfxGain);
    noiseSource.start(now);
  }

  // ── 4.3 Goal Scored ──────────────────────────────────────────────

  static playGoal(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    // Triumphant chord
    const freqs = [261.6, 329.6, 392.0, 523.3];
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0, now + i * 0.05);
      gain.gain.linearRampToValueAtTime(0.18, now + i * 0.05 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 2.0);
      osc.connect(gain);
      gain.connect(this._sfxGain!);
      osc.start(now + i * 0.05);
      osc.stop(now + 2.5);
    });

    // Crowd roar after goal
    setTimeout(() => this.playCrowdCheer(true), 200);
  }

  // ── 4.4 Crowd Cheer ──────────────────────────────────────────────

  static playCrowdCheer(loud: boolean = false): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._musicGain) return;
    const now = ctx.currentTime;
    const vol = loud ? 0.25 : 0.12;

    const noiseBuf = this._makeNoise(ctx, 3, "pink");
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;

    const bpf = ctx.createBiquadFilter();
    bpf.type = "bandpass";
    bpf.frequency.value = 600;
    bpf.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + 0.5);
    gain.gain.setValueAtTime(vol, now + 2.0);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 3.0);

    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(this._musicGain);
    src.start(now);
  }

  // ── 4.5 Whistle ──────────────────────────────────────────────────

  static playWhistle(long: boolean = false): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;
    const duration = long ? 1.5 : 0.4;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 2800;
    if (!long) {
      osc.frequency.setValueAtTime(2800, now);
      osc.frequency.linearRampToValueAtTime(3200, now + 0.1);
      osc.frequency.linearRampToValueAtTime(2600, now + 0.3);
    } else {
      // Long final whistle: multiple blasts
      osc.frequency.setValueAtTime(2800, now);
    }
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    
    // Add vibrato
    const vibrato = ctx.createOscillator();
    const vibratoGain = ctx.createGain();
    vibrato.frequency.value = 12;
    vibratoGain.gain.value = 60;
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);
    vibrato.start(now);
    vibrato.stop(now + duration);

    osc.connect(gain);
    gain.connect(this._sfxGain);
    osc.start(now);
    osc.stop(now + duration);
  }

  // ── 4.6 Footsteps ────────────────────────────────────────────────

  static playFootstep(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    const noiseBuf = this._makeNoise(ctx, 0.05, "white");
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;

    const lpf = ctx.createBiquadFilter();
    lpf.type = "lowpass";
    lpf.frequency.value = 800;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

    src.connect(lpf);
    lpf.connect(gain);
    gain.connect(this._sfxGain);
    src.start(now);
  }

  // ── 4.7 Ball Hit Post ────────────────────────────────────────────

  static playBallHitPost(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(800, now);
    osc.frequency.exponentialRampToValueAtTime(200, now + 0.3);
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc.connect(gain);
    gain.connect(this._sfxGain);
    osc.start(now);
    osc.stop(now + 0.5);
  }

  // ── 4.8 Goalkeeper Catch ─────────────────────────────────────────

  static playGKCatch(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    const noiseBuf = this._makeNoise(ctx, 0.15, "white");
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;

    const bpf = ctx.createBiquadFilter();
    bpf.type = "bandpass";
    bpf.frequency.value = 1200;
    bpf.Q.value = 2;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(this._sfxGain);
    src.start(now);
  }

  // ── 4.9 Referee Announcements ────────────────────────────────────

  static playRefereeBeep(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._sfxGain) return;
    const now = ctx.currentTime;

    // Two short beeps
    for (let i = 0; i < 2; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 1600;
      gain.gain.setValueAtTime(0.2, now + i * 0.2);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.2 + 0.15);
      osc.connect(gain);
      gain.connect(this._sfxGain!);
      osc.start(now + i * 0.2);
      osc.stop(now + i * 0.2 + 0.15);
    }
  }

  // ── 4.10 Weather Sounds ──────────────────────────────────────────

  static startRainSound(): void {
    const ctx = this._ctx_safe();
    if (!ctx || !this._musicGain || this._weatherNode) return;
    
    const noiseBuf = this._makeNoise(ctx, 3, "white");
    this._weatherNode = ctx.createBufferSource();
    this._weatherNode.buffer = noiseBuf;
    this._weatherNode.loop = true;
    
    const hpf = ctx.createBiquadFilter();
    hpf.type = "highpass";
    hpf.frequency.value = 3000;
    
    const gain = ctx.createGain();
    gain.gain.value = 0.08;
    
    this._weatherNode.connect(hpf);
    hpf.connect(gain);
    gain.connect(this._musicGain);
    this._weatherNode.start();
  }

  static stopWeatherSound(): void {
    if (this._weatherNode) {
      this._weatherNode.stop();
      this._weatherNode = null;
    }
  }

  // ── 4.12 Procedural Commentary ───────────────────────────────────

  private static _lastCommentaryTime = 0;
  private static _commentaryLines: { [event: string]: string[] } = {
    goal: [
      "GOAL! What a finish!",
      "Absolutely STUNNING strike!",
      "The net is bulging! GOAL!",
      "He shoots, he scores! Magnificent!",
      "Top corner! The goalkeeper had no chance!",
    ],
    save: [
      "What a save by the goalkeeper!",
      "Brilliant reflexes!",
      "He had to be at his best there!",
    ],
    tackle: [
      "Crunching tackle!",
      "He wins the ball back!",
      "Strong challenge there!",
    ],
    miss: [
      "So close! Just wide!",
      "He'll be disappointed with that one!",
      "The chance was there, but not taken!",
    ],
    opponentGoal: [
      "Conceded. Back to basics.",
      "The opponent finds the net.",
      "Unlucky there.",
    ],
  };

  static playCommentary(event: string): void {
    const now = Date.now();
    if (now - this._lastCommentaryTime < 5000) return; // Rate limit
    this._lastCommentaryTime = now;

    const lines = this._commentaryLines[event];
    if (!lines || lines.length === 0) return;

    const line = lines[Math.floor(Math.random() * lines.length)];
    
    // Use Web Speech API if available
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(line);
      utterance.rate = 1.1;
      utterance.pitch = 0.9;
      utterance.volume = this._settings.masterVolume * this._settings.commentaryVolume;
      window.speechSynthesis.speak(utterance);
    }
  }

  // ── Resume context on user interaction ───────────────────────────

  static resume(): void {
    if (this._ctx?.state === "suspended") {
      this._ctx.resume();
    }
  }

  static dispose(): void {
    this._ctx?.close();
    this._ctx = null;
    this._masterGain = null;
    this._sfxGain = null;
    this._musicGain = null;
    this._commentaryGain = null;
    this._ambientNode = null;
    this._weatherNode = null;
  }
}
